import { useContext, useState } from "react";
import { MetaforceContext, WebEditorContext } from "../../contexts";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { DocumentMagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { previewAttachment, searchAttachmentFromLibrary } from "../../api/webEditor";
import { useToastAction } from "../../hooks/useToastAction";
import * as Constants from "../../utils/constants"
import { useTranslation } from 'react-i18next';
import { resources, translations } from "../../translations/translationsConstants";

export default function AttachmentLibrary({ onClose }) {
    const { startParameter } = useContext(MetaforceContext);
    const { editorFileRef, languageCodeFromQueryString } = useContext(WebEditorContext)
    const [searchResult, setSearchResult] = useState();
    const [selectedAttachments, setSelectedAttachments] = useState([])
    const { t: ta } = useTranslation([resources.attachment]);
    const { t: terr } = useTranslation([resources.errors]);

    const searchAction = useToastAction();
    const loadAction = useToastAction();

    const toggleSelectAttachment = (attachment) => {
        setSelectedAttachments(prev => {
            const idx = selectedAttachments.findIndex(x => x === attachment);
            if (idx === -1) {
                return [...prev, attachment];
            }
            return [...prev.filter(item => item !== attachment)];
        })
    }

    const toggleAllAttachments = () => {
        selectedAttachments.length === searchResult?.Attachments?.length ?
            setSelectedAttachments([]) : setSelectedAttachments(searchResult?.Attachments)
    }

    const handleKeyUp = async (e) => {
        if (e.key === 'Enter') {
            searchAction.execute(async () => {
                const searchRequest = {
                    startParameter: startParameter,
                    text: e.target.value,
                }
                const res = await searchAttachmentFromLibrary(searchRequest, startParameter);
                setSearchResult(res);
            }, terr(translations.errors.failSearchAttachment));
        }
    }

    const handlePreviewAttachment = (publishedAttachmentId) => {
        loadAction.execute(async () => {
            const previewModel = {
                startParameter: startParameter,
                publishedAttachmentId: publishedAttachmentId,
            }
            const res = await previewAttachment(previewModel, startParameter);
            const blob = new Blob([res], { type: "application/pdf" });
            const newBlobUrl = URL.createObjectURL(blob);
            const pdfWindow = window.open();
            pdfWindow.location.href = newBlobUrl;
            // setBlobUrl(newBlobUrl);
        }, terr(translations.errors.failLoadFile));
    }

    return (
        <div className="sm:tw-min-h-full print:tw-my-0 print:tw-min-h-screen tw-bg-brand-background tw-overflow-y-auto tw-h-full">
            <div className="tw-max-w-screen-2xl tw-mx-auto tw-inset-0 tw-py-6 tw-px-4 md:tw-px-6 lg:tw-px-10">
                <div className="tw-py-5 ">
                    <h3 className="tw-text-lg tw-leading-6 tw-font-medium tw-text-gray-900 ">{ta(translations.attachment.attachmentLibrary)}</h3>

                    <div className="tw-flex tw-justify-start tw-mt-10">
                        <div className="tw-relative tw-rounded-md tw-shadow-sm">
                            <div className="tw-absolute tw-top-0 tw-left-0 tw-bottom-0 tw-h-5 tw-pl-3 tw-m-auto tw-mx-2 tw-text-gray-400" >
                                <MagnifyingGlassIcon className="tw-h-5 tw-w-5 tw-text-gray-400" />
                            </div>
                            <input
                                aria-label={ta(translations.attachment.search)}
                                type="text"
                                className="focus:tw-ring-indigo-700 focus:tw-border-indigo-700 tw-block tw-w-96 sm:tw-text-sm tw-border-gray-300 tw-rounded-md tw-pl-12 disabled:tw-cursor-not-allowed"
                                placeholder={ta(translations.attachment.search)}
                                onKeyUp={handleKeyUp}
                                disabled={editorFileRef.current?.DxmlFileStatus !== Constants.fileStatus.draft}
                            />
                        </div>
                    </div>

                    <div className="tw-shadow-sm tw-overflow-x-auto tw-overflow-y-hidden tw-border-b tw-border-gray-200 sm:tw-rounded-lg tw-my-4">
                        <table className="tw-min-w-full tw-divide-y tw-divide-gray-200">
                            <thead className="tw-bg-gray-50">
                                <tr>
                                    <th scope="col" className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider " >
                                        <input
                                            aria-label={((searchResult?.Attachments?.length > 0 && selectedAttachments.length === searchResult?.Attachments?.length) ?
                                                ta(translations.attachment.unselectAllAttachments) : ta(translations.attachment.selectAllAttachments))}
                                            id="selectAll"
                                            name="selectAll"
                                            type="checkbox"
                                            disabled={!searchResult || !searchResult.Attachments || searchResult.Attachments.length === 0 ? true : false}
                                            className="focus:tw-ring-indigo-700 tw-h-4 tw-w-4 tw-text-indigo-700 tw-border-gray-300 tw-rounded"
                                            checked={searchResult?.Attachments?.length > 0 && selectedAttachments.length === searchResult?.Attachments?.length}
                                            onChange={e => toggleAllAttachments()}
                                        />
                                    </th>

                                    <th scope="col" className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider " >
                                        {ta(translations.attachment.name)}
                                    </th>
                                    <th scope="col" className="tw-px-6 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider " >
                                        {ta(translations.attachment.description)}
                                    </th>
                                    <th scope="col" className="tw-relative tw-px-6 tw-py-3">
                                        <span className="tw-sr-only">{ta(translations.attachment.preview)}</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="tw-bg-white tw-divide-y tw-divide-gray-200">
                                {searchResult && searchResult.Attachments && searchResult.Attachments.map(dxmlAttachment => (
                                    <tr key={dxmlAttachment.PublishedAttachmentId}>
                                        <td className="tw-px-6 tw-py-3 tw-whitespace-nowrap tw-text-sm tw-text-gray-500">
                                            <input
                                                aria-label={((selectedAttachments.findIndex(i => i === dxmlAttachment) !== -1) ? ta(translations.attachment.unselectAttachment) : ta(translations.attachment.selectAttachment)) + ` ${dxmlAttachment.Name}`}
                                                id={dxmlAttachment.PublishedAttachmentId}
                                                name={`attachment-select-${dxmlAttachment.PublishedAttachmentId}`}
                                                type="checkbox"
                                                className="focus:tw-ring-indigo-700 tw-h-4 tw-w-4 tw-text-indigo-700 tw-border-gray-300 tw-rounded"
                                                checked={selectedAttachments.findIndex(i => i === dxmlAttachment) !== -1}
                                                onChange={() => toggleSelectAttachment(dxmlAttachment)}
                                            />
                                        </td>
                                        <td className="tw-px-6 tw-py-3 tw-whitespace-nowrap tw-text-sm tw-font-medium tw-text-gray-900" >
                                            {dxmlAttachment.Name}
                                        </td>
                                        <td className="tw-px-6 tw-py-3 tw-whitespace-nowrap tw-text-sm tw-text-gray-500">
                                            {dxmlAttachment.Description}
                                        </td>
                                        <td className="tw-px-3 tw-py-3 tw-whitespace-nowrap tw-text-sm tw-font-medium tw-text-gray-500">
                                            <div className="tw-grid tw-justify-center">
                                                <a
                                                    aria-label={`${ta(translations.attachment.previewAttachment)} ${dxmlAttachment.Name}`}
                                                    className="tw-p-1 tw-flex item-center tw-bg-white none-underline hover:tw-text-gray-600 focus:tw-ring-2 focus:tw-ring-indigo-700 tw-border tw-border-gray-300 tw-rounded-md"
                                                    // href={`/WebEditor/Preview/Pdf/Attachment/${startParameter}/${dxmlAttachment.PublishedAttachmentId}` + (languageCodeFromQueryString ? `?lng=${languageCodeFromQueryString}` : '')}
                                                    onClick={e => {
                                                        e.preventDefault();
                                                        handlePreviewAttachment(dxmlAttachment.PublishedAttachmentId);
                                                    }}
                                                    rel="noreferrer"
                                                    target="_blank"
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            e.target.click();
                                                        }
                                                    }}
                                                >
                                                    <DocumentMagnifyingGlassIcon className="tw-h-5 tw-w-6" />
                                                </a>
                                            </div>
                                        </td>
                                    </tr>
                                ))}


                            </tbody>
                        </table>
                    </div>

                    <div className="tw-flex tw-justify-end tw-gap-x-2">
                        <button
                            type="button"
                            onClick={() => onClose()}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    onClose();
                                }
                            }}
                            className="tw-w-1/2 sm:tw-w-48 tw-inline-flex tw-items-center tw-justify-center tw-px-3 tw-py-2 tw-rounded-md tw-border tw-border-gray-300 tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-gray-300 
                    hover:tw-bg-gray-200 focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-indigo-700 hover:tw-bg-gray-50 tw-cursor-pointer disabled:tw-cursor-not-allowed disabled:tw-bg-gray-200 disabled:tw-text-gray-400"
                        >
                            {ta(translations.attachment.back)}
                        </button>
                        <button
                            type="button"
                            onClick={() => onClose(selectedAttachments)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    onClose(selectedAttachments);
                                }
                            }}
                            className="tw-w-1/2 sm:tw-w-48 tw-inline-flex tw-items-center tw-justify-center tw-px-3 tw-py-2 tw-rounded-md tw-border tw-border-transparent tw-bg-indigo-700 tw-text-sm tw-font-medium tw-text-white 
                    hover:tw-bg-indigo-700-hover focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-indigo-700 disabled:tw-cursor-not-allowed disabled:tw-bg-gray-200 disabled:tw-text-gray-400"
                            disabled={editorFileRef.current?.DxmlFileStatus !== Constants.fileStatus.draft || selectedAttachments?.length === 0}
                        >
                            {ta(translations.attachment.save)}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}